import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { availType, Availability, availTime, shortByWeekday, defaultAvail } from "@caredna-marlin/utils/client/availability";
import { Box, Checkbox, Divider, IconButton, Menu, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { datef } from "@caredna-marlin/utils/dates";
import { Fragment, useState } from "react";
import { AvailabilityAction } from '~/hooks/useAvailabilityOperation';
import { AvailabilityTypeEnum } from "~/graphql/__generated__/graphql";
import AddAvailabilityMenu from "./AddAvailabilityMenu";
import { startOfToday } from "date-fns";

type Props = {
  availability: Availability[],
  selected?: number[],
  setEditing: RowProps["setEditing"],
  providerId?: number,
}

const AvailabilityTable = ({ availability, selected, setEditing, providerId }: Props) => {
  const hasAvailability = availability.length > 0;

  return <Box sx={{ mb: 2 }}>
    <Table
      sx={{ width: "100%", mb: 0, "td": { p: "0 8px", borderBottom: "1px dotted", borderBottomColor: "grey.300" }, "th": { p: "0 8px" }, "td:first-child, th:first-child": { paddingLeft: '32px' }, "tr:last-child td": { borderBottom: "none" }, "thead tr": { borderBottom: "none" }, "tbody tr:first-of-type td": { borderTop: "none" } }}
      size="small"
    >
      {hasAvailability && (
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100', borderBottom: "none" }}>
            <TableCell sx={{ fontWeight: 'normal', textTransform: 'uppercase', color: 'grey.600', pl: 0, width: '15%', borderBottom: "none" }}>Title</TableCell>
            <TableCell sx={{ fontWeight: 'normal', textTransform: 'uppercase', color: 'grey.600', width: '15%', borderBottom: "none" }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 'normal', textTransform: 'uppercase', color: 'grey.600', width: '20%', borderBottom: "none" }}>Duration</TableCell>
            <TableCell sx={{ fontWeight: 'normal', textTransform: 'uppercase', color: 'grey.600', width: '15%', borderBottom: "none" }}>Date Range</TableCell>
            <TableCell sx={{ fontWeight: 'normal', textTransform: 'uppercase', color: 'grey.600', width: '15%', borderBottom: "none" }}>Recurrence</TableCell>
            <TableCell sx={{ fontWeight: 'normal', textTransform: 'uppercase', color: 'grey.600', width: '15%', borderBottom: "none" }}>More Details</TableCell>
            {setEditing && <TableCell sx={{ fontWeight: 'normal', textTransform: 'uppercase', color: 'grey.600', textAlign: "right", width: '5%', borderBottom: "none" }}></TableCell>}
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {availability.map(avail => <AvailabilityTableRow
          avail={avail}
          key={avail.id}
          setEditing={setEditing}
          selected={selected?.includes(avail.id) || false}
          setSelected={null}
        />)}
      </TableBody>
    </Table>
    {setEditing && providerId && <AddAvailabilityButton setEditing={setEditing} providerId={providerId} />}
  </Box>
}

export default AvailabilityTable;




type RowProps = {
  avail: Availability,
  selected: boolean,
  // Show availability edit dropdown in this context
  setEditing: null | ((editing: { action: AvailabilityAction, avail: Availability }) => void),
  // Add checkbox to select / unselect this row
  setSelected: null | ((selected: boolean) => void),
}

export const AvailabilityTableRow = ({ avail, setEditing, selected, setSelected }: RowProps) => {
  return <TableRow selected={selected}>
    {setSelected && <TableCell sx={{ width: "4em", pl: 0 }}>
      <Checkbox checked={selected} onClick={() => setSelected(!selected)} />
    </TableCell>}
    <TableCell sx={{ pl: 0 }}>{avail.note || "N/A"}</TableCell>
    <TableCell>{availType(avail)}</TableCell>
    <TableCell>{availTime(avail)}</TableCell>
    <TableCell>
      {datef(avail.rrule.dtstart, "M/d/yy")}
      {avail.rrule.until ? (" - " + datef(avail.rrule.until, "M/d/yy")) : " +"}
    </TableCell>
    <TableCell>
      {shortByWeekday(avail)}
    </TableCell>
    <TableCell ><AvailExtraDetails avail={avail} /></TableCell>
    {setEditing && <TableCell sx={{ textAlign: "right" }}>
      <AvailabilityActionsMenu avail={avail} setEditing={setEditing} />
    </TableCell>}
  </TableRow>
}

const AvailExtraDetails = ({ avail }: { avail: Availability}) => {
  const { shiftCenters, visitTypes } = useOrgConfig();

  const extras = [
    avail.shiftCenterId ? shiftCenters[avail.shiftCenterId]?.name : "",
    avail.overflowShift ? "Overflow" : "",
    avail.addressStreet ? `${avail.addressStreet}, ${avail.addressCity}, ${avail.addressState}` : "",
    avail.blockFilters?.visitTypeIds?.length && (avail.blockFilters.visitTypeIds.length <= 2 ? avail.blockFilters.visitTypeIds.map(id => visitTypes[id]?.name).join(", ") : `${avail.blockFilters.visitTypeIds.length} visit types`),
  ];

  return extras.filter(Boolean).map((e, i) => <Fragment key={i} >
    {i === 0 ? null : <Divider sx={{ m: 0.5 }} />}
    {e}
  </Fragment>)
}

const AvailabilityActionsMenu = ({ avail, setEditing }: { avail: Availability, setEditing: NonNullable<Props["setEditing"]> }) => {
  const [actionsOpen, setActionsOpen] = useState<null | HTMLElement>(null);

  return <>
    <IconButton aria-label="Actions" onClick={(e) => setActionsOpen(e.currentTarget)}>
      <MoreHorizIcon />
    </IconButton>
    <Menu open={!!actionsOpen} onClose={() => setActionsOpen(null)} anchorEl={actionsOpen}>
      <MenuItem onClick={() => {setEditing({ action: "edit", avail }); setActionsOpen(null); }}>
        Edit
      </MenuItem>
      <MenuItem onClick={() => {setEditing({ action: "copy", avail }); setActionsOpen(null); }}>
        Copy
      </MenuItem>
      <MenuItem onClick={() => {setEditing({ action: "delete", avail }); setActionsOpen(null); }}>
        Delete
      </MenuItem>
    </Menu>
  </>
}

type AddAvailabilityButtonProps = {
  setEditing: NonNullable<Props["setEditing"]>,
  providerId: number,
}

const AddAvailabilityButton = ({ setEditing, providerId }: AddAvailabilityButtonProps) => {
  const [menuOpen, setMenuOpen] = useState<null | HTMLElement>(null);

  const handleMenuClick = (type: AvailabilityTypeEnum, blockType?: any) => {
    setEditing({ action: "create", avail: defaultAvail(type, startOfToday(), providerId, blockType) });
    setMenuOpen(null);
  };

  return (
    <Box sx={{ pl: 1 }}>
      <Button
        startIcon={<AddIcon />}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => setMenuOpen(e.currentTarget)}
        sx={{ 
          color: 'primary.main',
          fontWeight: 500,
          textTransform: 'none',
          justifyContent: 'flex-start',
          pl: 0,
          ml: 3 
        }}
      >
        Add Availability
      </Button>
      <Menu open={!!menuOpen} onClose={() => setMenuOpen(null)} anchorEl={menuOpen}>
        <AddAvailabilityMenu handleMenuClick={handleMenuClick} />
      </Menu>
    </Box>
  );
}
